import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Alert,
  AlertIcon,
  Button,
  VStack,
  Link,
  useColorModeValue,
  Badge,
  Flex,
  useToast,
  FormControl,
  FormLabel,
  Input,
  HStack,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { verifyEmail, resendVerificationEmail, checkUserStatus } from '../../api/auth';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import { useAuth } from '../../hooks/useAuth';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isAuthenticated, user, fetchUserProfile, logout } = useAuth();
  
  // Get email from navigation state (passed from signup page)
  const emailFromState = location.state?.email || '';
  
  const [email, setEmail] = useState(emailFromState);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  
  const formBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const linkColor = useColorModeValue('blue.500', 'blue.400');
  const separatorColor = useColorModeValue('gray.400', 'gray.500');

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Clear authentication state and navigate
  const clearAuthAndNavigate = async (path: string) => {
    try {
      // Use centralized logout to clear Zustand store and localStorage
      await logout();
    } finally {
      if (path !== '/login') {
        // If a different target was requested, navigate after logout
        navigate(path, { replace: true });
      }
    }
  };

  const handleBackToLogin = () => {
    clearAuthAndNavigate('/login');
  };

  const handleCreateNewAccount = () => {
    clearAuthAndNavigate('/signup');
  };

  const handleCodeComplete = async (code: string) => {
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setVerificationStatus('loading');
    setErrorMessage('');
    
    try {
      const result = await verifyEmail(email, code);
      
      setVerificationStatus('success');
      
      // Check if we received a JWT token (new flow)
      if (result.access_token) {
        console.log('Received JWT token from email verification, setting auth state...');
        
        // Store the token in localStorage (same as login flow)
        localStorage.setItem('token', result.access_token);
        
        // Store user data in localStorage
        const userData = {
          id: result.user_id,
          username: result.username,
          email: email,
          profile_exists: result.profile_exists || false,
        };
        localStorage.setItem('user', JSON.stringify(userData));
        
        toast({
          title: 'Email Verified!',
          description: 'Your email has been successfully verified. Logging you in...',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh user data with the new authentication
        await fetchUserProfile();
        
        // Redirect based on profile status
        setTimeout(() => {
          if (!result.profile_exists) {
            // Go directly to onboarding
            console.log('User authenticated but needs onboarding, redirecting to /onboarding');
            navigate('/onboarding', { replace: true });
          } else {
            // Profile exists, go to dashboard
            console.log('User authenticated with profile, redirecting to /dashboard');
            navigate('/dashboard', { replace: true });
          }
        }, 1500);
      } else {
        // Old flow - no JWT token received (shouldn't happen with updated backend)
        toast({
          title: 'Email Verified!',
          description: result.message || 'Your email has been successfully verified.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh user data if authenticated to get updated verification status
        let updatedUser = user;
        if (isAuthenticated()) {
          console.log('Refreshing user profile after verification...');
          updatedUser = await fetchUserProfile();
        }
        
        // Smart redirect based on authentication status
        setTimeout(() => {
          if (isAuthenticated() && updatedUser) {
            // User is already logged in (likely from registration)
            if (!updatedUser.profile_exists) {
              // Go directly to onboarding
              console.log('User authenticated but needs onboarding, redirecting to /onboarding');
              navigate('/onboarding', { replace: true });
            } else {
              // Profile exists, go to chat
              console.log('User authenticated with profile, redirecting to /chat');
              navigate('/chat', { replace: true });
            }
          } else {
            // Not logged in, go to login page
            console.log('User not authenticated, redirecting to /login');
            navigate('/login', { replace: true, state: { email } });
          }
        }, 2000);
      }
    } catch (error: any) {
      setVerificationStatus('error');
      setAttemptsRemaining(prev => Math.max(0, prev - 1));
      
      const errorMsg = error.message || error.error || 'Verification failed';
      setErrorMessage(errorMsg);
      
      // Check if code is expired
      const isExpired = errorMsg.toLowerCase().includes('expired');
      
      toast({
        title: isExpired ? 'Code Expired' : 'Invalid Code',
        description: isExpired 
          ? 'Your verification code has expired. Please request a new one.' 
          : `Invalid verification code. ${attemptsRemaining - 1} attempts remaining.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // If no attempts left, force cooldown
      if (attemptsRemaining <= 1) {
        setResendCooldown(300); // 5 minute cooldown
        toast({
          title: 'Too Many Attempts',
          description: 'Please wait 5 minutes before trying again.',
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (resendCooldown > 0) {
      toast({
        title: 'Please Wait',
        description: `You can request a new code in ${resendCooldown} seconds`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsResending(true);
    
    try {
      const result = await resendVerificationEmail(email);
      
      // Check if email is already verified
      if (result.message && result.message.includes('already verified')) {
        toast({
          title: 'Already Verified',
          description: 'Your email is already verified. Redirecting to login...',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        
        setTimeout(() => {
          navigate('/login', { replace: true, state: { email } });
        }, 2000);
      } else {
        toast({
          title: 'Code Sent',
          description: 'A new verification code has been sent to your email',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset attempts and set cooldown
        setAttemptsRemaining(5);
        setResendCooldown(60); // 60 second cooldown between resends
        setVerificationStatus('idle');
        setErrorMessage('');
      }
    } catch (error: any) {
      toast({
        title: 'Failed to Resend',
        description: error.message || 'Failed to resend verification code',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="var(--gradient-auth)"
      position="relative"
      py={16}
      display="flex"
      alignItems="center"
      overflow="hidden"
    >
      {/* Abstract background elements */}
      <Box position="absolute" top="-100px" left="-50px" w="300px" h="300px" borderRadius="full" bg="blue.100" opacity="0.3" filter="blur(70px)" />
      <Box position="absolute" bottom="-100px" right="-50px" w="350px" h="350px" borderRadius="full" bg="purple.100" opacity="0.3" filter="blur(70px)" />
      
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Flex direction="column" alignItems="center">
          <Badge 
            mb={6} 
            px={3} 
            py={1} 
            colorScheme="blue" 
            borderRadius="full" 
            fontSize="sm"
          >
            Email Verification
          </Badge>
          
          <Heading 
            as="h1" 
            size="xl" 
            mb={6} 
            textAlign="center"
            bgGradient="linear(to-r, blue.500, purple.500)"
            bgClip="text"
            lineHeight="1.2"
            fontWeight="bold"
          >
            Verify Your Email
          </Heading>

          <Box
            w="full"
            maxW="md"
            mx="auto"
            p={6}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            boxShadow="lg"
            bg={formBg}
          >
            <VStack spacing={6}>
              {/* Instructions */}
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Check Your Email</Text>
                  <Text fontSize="sm">
                    We've sent a 6-digit verification code to your email address. 
                    Enter it below to verify your account.
                  </Text>
                </Box>
              </Alert>

              {/* Email Input */}
              <FormControl>
                <FormLabel fontSize="sm">Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  size="md"
                  focusBorderColor="blue.500"
                  autoComplete="email"
                />
              </FormControl>

              {/* Verification Code Input */}
              <FormControl>
                <FormLabel fontSize="sm" textAlign="center">Verification Code</FormLabel>
                <VerificationCodeInput
                  onComplete={handleCodeComplete}
                  disabled={verificationStatus === 'loading' || !email || attemptsRemaining === 0}
                  error={verificationStatus === 'error'}
                />
                {attemptsRemaining < 5 && attemptsRemaining > 0 && (
                  <Text fontSize="xs" color="orange.500" mt={2} textAlign="center">
                    {attemptsRemaining} attempts remaining
                  </Text>
                )}
              </FormControl>

              {/* Success Message */}
              {verificationStatus === 'success' && (
                <Alert status="success" borderRadius="md">
                  <AlertIcon as={CheckCircleIcon} />
                  <Box>
                    <Text fontWeight="bold">Email Verified Successfully!</Text>
                    <Text fontSize="sm">
                      Redirecting to login page...
                    </Text>
                  </Box>
                </Alert>
              )}

              {/* Error Message removed per request; toasts still display errors */}

              {/* Resend Button */}
              <Button
                colorScheme="gray"
                variant="outline"
                size="md"
                width="full"
                onClick={handleResendVerification}
                isLoading={isResending}
                loadingText="Sending..."
                isDisabled={resendCooldown > 0}
              >
                {resendCooldown > 0 
                  ? `Resend Code (${resendCooldown}s)` 
                  : 'Resend Verification Code'
                }
              </Button>

              {/* Code Expiry Notice */}
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Verification codes expire after 10 minutes
              </Text>

              {/* Navigation Links */}
              <VStack spacing={2}>
                <HStack spacing={4}>
                  <Link 
                    as="button" 
                    onClick={handleBackToLogin} 
                    color={linkColor}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    Back to Login
                  </Link>
                  <Text color={separatorColor}>•</Text>
                  <Link 
                    as="button" 
                    onClick={handleCreateNewAccount} 
                    color={linkColor}
                    _hover={{ textDecoration: 'underline' }}
                  >
                    Create New Account
                  </Link>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default EmailVerificationPage;
